import type { FormField } from '../../interfaces/form.interface';
import type {
    Input,
    RepeatableGroupConfiguration,
    RepeatableGroupDomainAdapter,
    RepeatableLegacyAggregateSerialization,
    RepeatableLegacySlotSerialization,
    RepeatablePendingDropdownResolutionConfiguration,
    RepeatableSubField,
} from '../../interfaces/input.interface';
import { InputType as ReactiveInputType } from '../../interfaces/input.interface';
import {
    ReactiveFieldFactory,
    type ReactiveBaseFieldFactoryConfig,
} from '../factory/reactive-field-factory';

const DEFAULT_REPEATABLE_SEPARATOR = ';';
const DEFAULT_REPEATABLE_FIELD_WIDTH = 12;

export interface ReactiveRepeatableGroupFactoryConfig<TModel> extends Omit<ReactiveBaseFieldFactoryConfig<TModel>, 'input'> {
    readonly rows: number;
    readonly addButtonLabel: string;
    readonly bindings: Record<string, string>;
    readonly subFields: RepeatableSubField[];
    readonly serialization?: RepeatableGroupConfiguration['serialization'];
    readonly parentDependencies?: string | readonly string[];
    readonly pendingDropdownResolution?: RepeatablePendingDropdownResolutionConfiguration;
    readonly forceStackedLayout?: boolean;
    readonly domainAdapter?: RepeatableGroupDomainAdapter;
    readonly legacySlotSerialization?: RepeatableLegacySlotSerialization;
    readonly legacyAggregateSerialization?: RepeatableLegacyAggregateSerialization;
    readonly input?: Partial<Omit<Input<TModel>, 'isEditable' | 'repeatableGroupConfiguration' | 'type'>>;
}

/** Builds a legacy-compatible repeatable group field without repeating input and condition boilerplate. */
export class ReactiveRepeatableGroupFactory {
    public static create<TModel>(config: ReactiveRepeatableGroupFactoryConfig<TModel>): FormField<TModel> {
        const input = ReactiveFieldFactory.createInput<TModel>(ReactiveInputType.REPEATABLE_GROUP, {
            ...config,
            input: {
                ...(config.input ?? {}),
                repeatableGroupConfiguration: this.createConfiguration(config),
            },
        });

        return ReactiveFieldFactory.createWithInput(
            {
                ...config,
                width: config.width ?? DEFAULT_REPEATABLE_FIELD_WIDTH,
            },
            input,
        );
    }

    public static createConfiguration<TModel>(
        config: ReactiveRepeatableGroupFactoryConfig<TModel>,
    ): RepeatableGroupConfiguration {
        return {
            rows: config.rows,
            addButtonLabel: config.addButtonLabel,
            bindings: config.bindings,
            serialization: config.serialization ?? { separator: DEFAULT_REPEATABLE_SEPARATOR },
            subFields: config.subFields,
            ...(config.parentDependencies !== undefined ? { parentDependencies: config.parentDependencies } : {}),
            ...(config.pendingDropdownResolution !== undefined ? {
                pendingDropdownResolution: config.pendingDropdownResolution,
            } : {}),
            ...(config.forceStackedLayout !== undefined ? { forceStackedLayout: config.forceStackedLayout } : {}),
            ...(config.domainAdapter !== undefined ? { domainAdapter: config.domainAdapter } : {}),
            ...(config.legacySlotSerialization !== undefined ? { legacySlotSerialization: config.legacySlotSerialization } : {}),
            ...(config.legacyAggregateSerialization !== undefined ? {
                legacyAggregateSerialization: config.legacyAggregateSerialization,
            } : {}),
        };
    }
}
